import { useEffect, useRef, useState } from 'react'
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '../common'
import { Popover } from '../common/overlays/Popover'
import { formatDateRangeLabel, localDateKey, parseDateKey } from './dashboardFilters'
import styles from './DateRangePicker.module.css'

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

function monthLabel(date) {
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

function dayLabel(date) {
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
}

function firstOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function shiftMonth(date, months) {
  return new Date(date.getFullYear(), date.getMonth() + months, 1)
}

// The six weeks that show a month: every day from the Sunday on or before
// the first to the Saturday on or after the last, padded to six rows so the
// grid keeps one height as the months change.
function weeksOf(month) {
  const first = firstOfMonth(month)
  const cursor = new Date(first)
  cursor.setDate(1 - first.getDay())
  const weeks = []
  for (let week = 0; week < 6; week += 1) {
    const days = []
    for (let day = 0; day < 7; day += 1) {
      days.push(new Date(cursor))
      cursor.setDate(cursor.getDate() + 1)
    }
    weeks.push(days)
  }
  return weeks
}

// A range of whole days picked from a calendar, in place of two native date
// inputs that look different in every browser and cannot show the range.
// The first click starts a range, the second ends it (either order), and a
// third starts over. Days after today cannot be picked: the dashboard has
// nothing to say about them. `start` and `end` are YYYY-MM-DD strings, as
// the URL keeps them.
export function DateRangePicker({ start, end, onChange, compact = false, today = new Date() }) {
  const [open, setOpen] = useState(false)
  const [month, setMonth] = useState(() => firstOfMonth(parseDateKey(end) || parseDateKey(start) || today))
  const gridRef = useRef(null)
  const todayKey = localDateKey(today)

  // Opening the picker shows the month of the range it holds, not the one
  // it was last left on.
  useEffect(() => {
    if (open) setMonth(firstOfMonth(parseDateKey(end) || parseDateKey(start) || today))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const pick = (key) => {
    if (!start || end) {
      onChange({ start: key, end: '' })
      return
    }
    const [from, to] = key < start ? [key, start] : [start, key]
    onChange({ start: from, end: to })
    setOpen(false)
  }

  // Arrow keys walk the days; PageUp and PageDown turn the month.
  const onGridKeyDown = (event) => {
    const steps = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }
    if (event.key === 'PageUp' || event.key === 'PageDown') {
      event.preventDefault()
      setMonth((current) => shiftMonth(current, event.key === 'PageUp' ? -1 : 1))
      return
    }
    if (!(event.key in steps)) return
    const days = [...gridRef.current.querySelectorAll('[data-day]:not(:disabled)')]
    const index = days.indexOf(event.target)
    if (index < 0) return
    event.preventDefault()
    days[Math.max(0, Math.min(days.length - 1, index + steps[event.key]))]?.focus()
  }

  const label = start && end
    ? formatDateRangeLabel(start, end)
    : start ? `From ${parseDateKey(start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – pick an end date` : 'Pick dates'

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      placement="bottom-start"
      label="Custom date range"
      contentClassName={styles.surface}
      trigger={(
        <button type="button" className={`${styles.trigger} ${compact ? styles.compact : ''}`.trim()} aria-label="Custom dates">
          <CalendarDays size={16} aria-hidden="true" />
          <span className={styles.triggerLabel}>{label}</span>
        </button>
      )}
    >
      <div className={styles.calendar}>
        <div className={styles.monthNav}>
          <button type="button" className={styles.navButton} aria-label="Previous month" onClick={() => setMonth((current) => shiftMonth(current, -1))}>
            <ChevronLeft size={16} aria-hidden="true" />
          </button>
          <span className={styles.monthTitle} aria-live="polite">{monthLabel(month)}</span>
          <button
            type="button"
            className={styles.navButton}
            aria-label="Next month"
            disabled={shiftMonth(month, 1) > today}
            onClick={() => setMonth((current) => shiftMonth(current, 1))}
          >
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        </div>
        <div className={styles.weekdays} aria-hidden="true">
          {WEEKDAYS.map((day) => <span key={day}>{day}</span>)}
        </div>
        <div ref={gridRef} className={styles.grid} role="grid" aria-label={monthLabel(month)} onKeyDown={onGridKeyDown}>
          {weeksOf(month).map((week) => (
            <div key={localDateKey(week[0])} role="row" className={styles.week}>
              {week.map((date) => {
                const key = localDateKey(date)
                const outside = date.getMonth() !== month.getMonth()
                // A half-picked range is anchored on its start, so that
                // cell is both ends until the second click.
                const isStart = key === start
                const isEnd = key === (end || start || '')
                const inRange = start && end && key > start && key < end
                return (
                  <span key={key} role="gridcell" className={styles.cell} data-in-range={inRange || undefined} data-start={isStart || undefined} data-end={isEnd || undefined}>
                    <button
                      type="button"
                      className={styles.day}
                      data-day=""
                      data-outside={outside || undefined}
                      data-today={key === todayKey || undefined}
                      aria-label={dayLabel(date)}
                      aria-pressed={isStart || isEnd || undefined}
                      disabled={key > todayKey}
                      onClick={() => pick(key)}
                    >
                      {date.getDate()}
                    </button>
                  </span>
                )
              })}
            </div>
          ))}
        </div>
        <div className={styles.footer}>
          <span className={styles.summary}>{start && end ? formatDateRangeLabel(start, end) : start ? 'Now pick the end date' : 'Pick the first day'}</span>
          {start || end ? (
            <Button variant="ghost" size="compact" onClick={() => onChange({ start: '', end: '' })}>Clear</Button>
          ) : null}
        </div>
      </div>
    </Popover>
  )
}
